import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import axios from "axios";

// Put a trainer on a plan directly, with no payment. Effective the same day:
// the API starts the new plan's monthly slots today, so it renews on today's
// date each month exactly as a purchased plan would.
const SetPlan = ({ user, onDone }) => {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    axios.get(API_ENDPOINT + "admin/get-plans", getHeader()).then((res) => {
      const d = res.data;
      if (d.success) setPlans(d.message || []);
    }).catch(() => toast("Couldn't load the plan list"));
    setSelected("");
    setConfirming(false);
  }, [user]);

  const current = user && user.plan;
  const chosen = plans.find((p) => p.name === selected);

  // Mirrors the API: the current month's remainder plus every month not yet
  // started moves into the permanent top-up pool.
  const carryOver = (() => {
    if (!current || current.status !== "Active" || !Array.isArray(current.slots)) return 0;
    const today = new Date();
    if (current.endsOn && new Date(current.endsOn) < today) return 0;
    const total = current.slots
      .filter((s) => new Date(s.end_date) >= today)
      .reduce((sum, s) => sum + ((s.count || 0) - (s.used || 0)), 0);
    // Clamped exactly as the API clamps it. An account migrated onto a smaller
    // allowance can have used more than it now includes, which would otherwise
    // preview as a negative carry-over.
    return Math.max(0, total);
  })();

  const apply = () => {
    if (!selected) { toast("Choose a plan first"); return; }
    setLoading(true);
    axios.post(API_ENDPOINT + "admin/set-trainer-plan", { phone: user.phone, planName: selected }, getHeader())
      .then((res) => {
        setLoading(false);
        const d = res.data;
        toast(d.message, { autoClose: 2500, position: "top-right" });
        if (d.success) { setConfirming(false); if (onDone) onDone(); }
      })
      .catch(() => { setLoading(false); toast("Request failed"); });
  };

  return (
    <div style={{ maxWidth: 600, margin: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 18px", fontSize: 14, marginBottom: 18 }}>
        <div className="text-muted">Name</div>
        <div style={{ fontWeight: 600 }}>{user && user.name}</div>
        <div className="text-muted">Current plan</div>
        <div style={{ fontWeight: 600 }}>
          {current ? current.name : "Not subscribed"}
          {current ? (
            <span className={current.status === "Expired" ? "tag tag-neutral" : "tag tag-accent"} style={{ marginLeft: 8 }}>
              {current.status}
            </span>
          ) : null}
        </div>
        <div className="text-muted">Attempts</div>
        <div>{current ? `${current.candidateCount ?? current.assessmentCount ?? "—"} / month` : "—"}</div>
        <div className="text-muted">AI questions</div>
        <div>{current && current.aiQuestions != null ? `${current.aiQuestions} / month` : "—"}</div>
      </div>

      <hr className="hr" />

      <div className="field" style={{ marginBottom: 12 }}>
        <label>Set plan to</label>
        <select className="input" value={selected} onChange={(e) => { setSelected(e.target.value); setConfirming(false); }}>
          <option value="">Choose a plan…</option>
          {plans.map((p) => (
            <option key={p._id || p.name} value={p.name}>
              {p.name} · {p.count} attempts · {p.aiQuestions != null ? `${p.aiQuestions} AI` : "AI default"}
              {p.isActive ? "" : " (not sold)"}
            </option>
          ))}
        </select>
      </div>

      {chosen ? (
        <div style={{ background: "var(--color-surface)", border: "1px solid var(--color-divider)", padding: 14, fontSize: 13, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>What will happen</div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>Takes effect <strong>today</strong> — the plan renews on today's date each month.</li>
            <li>{chosen.count} attempts and {chosen.aiQuestions != null ? chosen.aiQuestions : "the default"} AI questions per month, for 12 months.</li>
            <li>
              {carryOver > 0
                ? <>Their <strong>{carryOver}</strong> unused attempt(s) move into top-up, which never expires.</>
                : "Nothing unused to carry over."}
            </li>
            <li>No payment is taken and no invoice is raised.</li>
          </ul>
        </div>
      ) : null}

      {confirming ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--color-accent)", flex: 1 }}>
            Replace {current ? current.name : "no plan"} with {selected}?
          </span>
          <button className="btn btn-primary" onClick={apply} disabled={loading}>
            {loading ? "Applying…" : "Yes, set plan"}
          </button>
          <button className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={loading}>Cancel</button>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={() => setConfirming(true)} disabled={!selected} style={{ width: "100%" }}>
          Set subscription
        </button>
      )}
    </div>
  );
};

export default SetPlan;
